import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Button } from '@@/ui/primitives';

import {
  LogsIcon,
  InfoIcon,
  MinusIcon,
  Maximize2Icon,
  BrainIcon,
  AlertTriangleIcon,
  ChevronsUpDownIcon,
  ChevronsDownUpIcon,
  GitBranchIcon
} from 'lucide-react';
import { TabButton } from './tab-button';
import { IvrLogViewer } from './interaction-log-viewer';
import { ContactDetails } from './contact-details';
import { WisdomTranscript } from './wisdom-transcript';
import { Problems } from './problems';
import { FlowLogViewer } from './flow-log-viewer';
import { ProblemDetector } from './problems/problem-detector';
import { LogType } from '../../types/visualizer';
import { clsx } from 'clsx';
import { useLocalStorage } from '@@/hooks/use-local-storage';
import { QAssistantLogViewer } from './q-assistant-log-viewer';
import { ToolCitationsViewer } from './tool-citations-viewer';
import { useLogNavigation } from '../../contexts/log-navigation-context';
import { Contact } from '@aws-sdk/client-connect';
import { useGetRealTimeLogs } from '../../hooks/use-get-realtime-logs';

export enum TabType {
  INTERACTION_LOGS = 'INTERACTION_LOGS',
  DETAILS = 'DETAILS',
  WISDOM_TRANSCRIPT = 'WISDOM_LOGS',
  Q_ASSISTANT_LOGS = 'Q_ASSISTANT_LOGS',
  PROBLEMS = 'PROBLEMS',
  FLOW_LOGS = 'FLOW_LOGS',
  TOOL_CITATIONS = 'TOOL_CITATIONS'
}

enum PanelState {
  CLOSED = 'CLOSED',
  NORMAL = 'NORMAL',
  EXPANDED = 'EXPANDED',
  MINIMIZED = 'MINIMIZED'
}

const DEFAULT_HEIGHT = 512;
const MIN_HEIGHT = 200;
const MAX_HEIGHT = 930;

export const ControlBar = (props: { contactId: string; contact: Contact }) => {
  const { data: contactFlowLogs } = useGetRealTimeLogs<'CONTACT_FLOW'>({
    contactId: props.contactId,
    kind: 'CONTACT_FLOW',
    startAt: props.contact.InitiationTimestamp!,
    endAt: props.contact.DisconnectTimestamp ?? null
  });

  const { data: interactionLogs } = useGetRealTimeLogs<'INTERACTION'>({
    contactId: props.contactId,
    kind: 'INTERACTION',
    date: (props.contact.InitiationTimestamp as unknown as string).split('T')[0]
  });

  const [activeTab, setActiveTab] = useState<TabType>(TabType.DETAILS);
  const [panelState, setPanelState] = useLocalStorage<PanelState>('cv-control-bar-state', PanelState.MINIMIZED);
  const [contentHeight, setContentHeight] = useLocalStorage<number>('cv-control-bar-height', DEFAULT_HEIGHT);
  const [lastNormalHeight, setLastNormalHeight] = useLocalStorage<number>(
    'cv-control-bar-last-normal-height',
    DEFAULT_HEIGHT
  );
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  const { activeTab: navigationActiveTab, navigationTrigger } = useLogNavigation();

  useEffect(() => {
    // navigationTrigger is used here in order to force a re-render.
    // If this project used a proper state management lib, we could have
    // used that instead.
    if (navigationActiveTab && navigationTrigger > 0) {
      setActiveTab(navigationActiveTab);
      setPanelState(PanelState.NORMAL);
    }
  }, [navigationTrigger]);

  const problemCount = useMemo(() => {
    const interactionProblems = ProblemDetector.detectProblems({
      logs: interactionLogs?.object?.InteractionLogs || [],
      wisdomMessages: []
    });

    const flowProblems = ProblemDetector.detectProblems({
      logs: [],
      wisdomMessages: [],
      flowLogs: contactFlowLogs?.events || []
    });

    return interactionProblems.length + flowProblems.length;
  }, [interactionLogs, contactFlowLogs]);

  const handleTabClick = (tab: TabType) => {
    if (panelState === PanelState.CLOSED) {
      setPanelState(PanelState.NORMAL);
      setActiveTab(tab);
    } else if (panelState === PanelState.MINIMIZED) {
      setPanelState(PanelState.NORMAL);
      setActiveTab(tab);
    } else if (activeTab === tab) {
      setPanelState(PanelState.CLOSED);
    } else {
      setActiveTab(tab);
    }
  };

  const handleExpansion = () => {
    if (panelState === PanelState.EXPANDED) {
      setContentHeight(lastNormalHeight ?? DEFAULT_HEIGHT);
      setPanelState(PanelState.NORMAL);
    } else if (panelState === PanelState.NORMAL) {
      setLastNormalHeight(contentHeight ?? DEFAULT_HEIGHT);
      setPanelState(PanelState.EXPANDED);
    }
  };

  const handleMinimize = () => {
    if (panelState === PanelState.MINIMIZED) {
      setPanelState(PanelState.NORMAL);
    } else {
      setPanelState(PanelState.MINIMIZED);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (panelState !== PanelState.NORMAL) return;

    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartHeight.current = contentHeight ?? DEFAULT_HEIGHT;
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = dragStartY.current - e.clientY;
      const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, dragStartHeight.current + deltaY));
      setContentHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, setContentHeight]);

  const renderTabContent = () => {
    if (!activeTab) return null;
    if (panelState === PanelState.MINIMIZED) {
      return null;
    }

    const sessionArnParts = props.contact.WisdomInfo?.SessionArn ? props.contact.WisdomInfo?.SessionArn.split('/') : [];
    const qicAssistantId = sessionArnParts[1];
    const qicSessionId = sessionArnParts[2];

    const tabComponents = {
      [TabType.DETAILS]: <ContactDetails contact={props.contact} />,
      [TabType.FLOW_LOGS]: contactFlowLogs ? <FlowLogViewer contactFlowLogs={contactFlowLogs.events} /> : null,
      [TabType.INTERACTION_LOGS]: interactionLogs ? (
        <IvrLogViewer logs={interactionLogs.object?.InteractionLogs} />
      ) : null,
      [TabType.WISDOM_TRANSCRIPT]: <WisdomTranscript assistantId={qicAssistantId} sessionId={qicSessionId} />,
      [TabType.Q_ASSISTANT_LOGS]: <QAssistantLogViewer contact={props.contact} />,
      [TabType.TOOL_CITATIONS]: <ToolCitationsViewer contact={props.contact} />,
      [TabType.PROBLEMS]: (
        <Problems
          interactionLogs={interactionLogs?.object?.InteractionLogs || []}
          logType={LogType.INTERACTION_LOG}
          wisdomMessages={[]}
          flowLogs={contactFlowLogs?.events || []}
        />
      )
    };

    return tabComponents[activeTab];
  };

  return (
    <div className='flex flex-col border-t border-neutral-200 overflow-hidden'>
      {/* Drag Handle */}
      {panelState === PanelState.NORMAL && (
        <div
          className={clsx(
            'h-1 bg-white',
            'transition-colors z-50',
            isDragging ? 'bg-blue-100 cursor-row-resize' : 'hover:bg-neutral-100 cursor-row-resize'
          )}
          onMouseDown={handleMouseDown}
          role='separator'
          aria-orientation='horizontal'
          aria-label='Resize panel'
        />
      )}
      {/* Navigation */}
      <div
        className={clsx(
          'min-h-10 h-10 bg-white border-neutral-200',
          panelState === PanelState.EXPANDED && 'border-t',
          'flex items-center justify-between px-3'
        )}
      >
        <div className={clsx('flex items-center gap-4', panelState === PanelState.NORMAL && '-mt-1')}>
          <TabButton
            icon={InfoIcon}
            label='Details'
            isActive={activeTab === TabType.DETAILS}
            isVisible={panelState !== PanelState.CLOSED}
            disabled={!props.contact}
            onClick={() => handleTabClick(TabType.DETAILS)}
            ariaLabel='Toggle Details'
          />
          <TabButton
            icon={AlertTriangleIcon}
            label='Problems'
            isActive={activeTab === TabType.PROBLEMS}
            isVisible={panelState !== PanelState.CLOSED}
            disabled={false}
            onClick={() => handleTabClick(TabType.PROBLEMS)}
            alertCount={problemCount}
            ariaLabel='Toggle Problems'
          />
          <TabButton
            icon={GitBranchIcon}
            label='Flow Logs'
            isActive={activeTab === TabType.FLOW_LOGS}
            isVisible={panelState !== PanelState.CLOSED}
            disabled={!contactFlowLogs || !contactFlowLogs.events.length}
            onClick={() => handleTabClick(TabType.FLOW_LOGS)}
            ariaLabel='Toggle Flow Logs'
          />
          <TabButton
            icon={LogsIcon}
            label='Interaction Logs'
            isActive={activeTab === TabType.INTERACTION_LOGS}
            isVisible={panelState !== PanelState.CLOSED}
            disabled={!interactionLogs || !interactionLogs.object?.InteractionLogs.length}
            onClick={() => handleTabClick(TabType.INTERACTION_LOGS)}
            ariaLabel='Toggle Logs'
          />
          <TabButton
            icon={BrainIcon}
            label='Q Transcript'
            isActive={activeTab === TabType.WISDOM_TRANSCRIPT}
            isVisible={panelState !== PanelState.CLOSED}
            disabled={!props.contact.WisdomInfo?.SessionArn}
            onClick={() => handleTabClick(TabType.WISDOM_TRANSCRIPT)}
            ariaLabel='Toggle Wisdom Logs'
          />
          <TabButton
            icon={BrainIcon}
            label='Q Assistant Logs'
            isActive={activeTab === TabType.Q_ASSISTANT_LOGS}
            isVisible={panelState !== PanelState.CLOSED}
            onClick={() => handleTabClick(TabType.Q_ASSISTANT_LOGS)}
            ariaLabel='Toggle Q Assistant Logs'
          />
          <TabButton
            icon={BrainIcon}
            label='Tool & Citations'
            isActive={activeTab === TabType.TOOL_CITATIONS}
            isVisible={panelState !== PanelState.CLOSED}
            disabled={!props.contact.WisdomInfo?.SessionArn}
            onClick={() => handleTabClick(TabType.TOOL_CITATIONS)}
            ariaLabel='Toggle Tool & Citations'
          />
        </div>
        <div>
          <Button
            variant='ghost'
            size='sm'
            className='text-sm! h-6 px-2'
            onClick={handleExpansion}
            disabled={[PanelState.MINIMIZED, PanelState.CLOSED].includes(panelState ?? PanelState.CLOSED)}
            aria-label={panelState === PanelState.EXPANDED ? 'Collapse Panel' : 'Expand Panel'}
          >
            {panelState === PanelState.EXPANDED ? (
              <ChevronsDownUpIcon className='size-4' />
            ) : (
              <ChevronsUpDownIcon className='size-4' />
            )}
          </Button>
          <Button
            variant='ghost'
            size='sm'
            className='text-sm! h-6 px-2'
            onClick={handleMinimize}
            aria-label={panelState === PanelState.MINIMIZED ? 'Restore Panel' : 'Minimize Panel'}
          >
            {panelState === PanelState.MINIMIZED ? (
              <Maximize2Icon className='size-4' />
            ) : (
              <MinusIcon className='size-4' />
            )}
          </Button>
        </div>
      </div>
      {/* Content */}
      <div
        className={clsx(
          'bg-white',
          !isDragging && 'transition-height duration-200',
          panelState &&
            ![PanelState.MINIMIZED, PanelState.CLOSED].includes(panelState) &&
            'border-t border-neutral-200',
          panelState === PanelState.EXPANDED
            ? 'h-[calc(100vh-146px)] min-h-96'
            : panelState === PanelState.CLOSED || panelState === PanelState.MINIMIZED
              ? '!h-0'
              : ''
        )}
        style={panelState === PanelState.NORMAL ? { height: `${contentHeight}px` } : undefined}
      >
        {renderTabContent()}
      </div>
    </div>
  );
};
